import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Search, Subscriptions as SubscriptionsIcon } from '@mui/icons-material';
import api from '../services/api';

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async (search = '') => {
    try {
      setLoading(true);
      const params = search ? { search } : {};
      const response = await api.get('/sites/all/subscriptions', { params });
      setSubscriptions(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(
      setTimeout(() => {
        fetchSubscriptions(value);
      }, 500)
    );
  };

  const columns = [
    { field: 'subscription_name', headerName: 'Subscription', flex: 1, minWidth: 250 },
    { field: 'root_domain', headerName: 'Domain', width: 180 },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Unknown'}
          color={params.value === 'Active' ? 'success' : 'default'}
          size="small"
        />
      )
    },
    { field: 'product', headerName: 'Product', width: 150 },
    { field: 'quickbooks', headerName: 'QuickBooks', width: 200 },
    { field: 'launch_date', headerName: 'Launch Date', width: 120 },
    { field: 'cancellation_date', headerName: 'Cancelled', width: 120 },
    {
      field: 'date_created',
      headerName: 'Created',
      width: 110,
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : ''
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2,
            bgcolor: 'primary.main', display: 'flex',
            alignItems: 'center', justifyContent: 'center', mr: 2,
            boxShadow: '0 4px 12px rgba(23, 75, 106, 0.25)'
          }}>
            <SubscriptionsIcon sx={{ fontSize: 24, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h4" component="h1" sx={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontWeight: 500, letterSpacing: '-0.01em'
            }}>
              Subscriptions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              All subscriptions synced from Monday.com — {subscriptions.length} total
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{
        p: 3, borderRadius: 3,
        boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)',
        border: '1px solid',
        borderColor: 'grey.200'
      }}>
        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by name, domain, product, or status..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'grey.400' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'grey.100',
                borderRadius: 2,
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'grey.300' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
              }
            }}
          />
        </Box>

        {/* Data Grid */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={36} sx={{ color: 'primary.main' }} />
          </Box>
        ) : (
          <DataGrid
            rows={subscriptions}
            columns={columns}
            autoHeight
            pageSize={25}
            rowsPerPageOptions={[25, 50, 100]}
            disableSelectionOnClick
            sx={{
              border: 'none',
              minHeight: 500,
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'grey.100',
                borderRadius: '8px 8px 0 0',
                borderBottom: '2px solid',
                borderColor: 'grey.200',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: '0.8rem',
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
                color: 'text.secondary'
              },
              '& .MuiDataGrid-cell': {
                py: 1.5,
                borderColor: 'grey.100',
                fontSize: '0.875rem'
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: 'rgba(23, 75, 106, 0.04)'
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '2px solid',
                borderColor: 'grey.200'
              }
            }}
          />
        )}
      </Paper>
    </Container>
  );
};

export default Subscriptions;
